import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/en';

dayjs.locale('en');

const ENGLISH_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export const DEFAULT_ORDER_TIME_SLOTS = [
  '08:00 AM',
  '09:00 AM',
  '10:00 AM',
  '11:00 AM',
  '12:00 PM',
  '01:00 PM',
  '02:00 PM',
  '03:00 PM',
  '04:00 PM',
  '05:00 PM',
  '06:00 PM',
  '07:00 PM',
] as const;

const TRIGGER_CLASS =
  'w-full h-8 px-2.5 rounded-md border text-xs outline-none bg-white transition-colors text-left flex items-center gap-1.5 cursor-pointer';

export interface EnglishDateInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  placeholder?: string;
  pickerTitle?: string;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

export interface EnglishDateTimeInputProps {
  id?: string;
  date: string;
  time: string;
  onChange: (date: string, time: string) => void;
  timeSlots?: readonly string[];
  min?: string;
  placeholder?: string;
  pickerTitle?: string;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

function daysInMonth(year: number, monthIndex: number): number {
  return dayjs().year(year).month(monthIndex).daysInMonth();
}

function toIsoDate(year: number, monthIndex: number, day: number): string {
  return dayjs().year(year).month(monthIndex).date(day).format('YYYY-MM-DD');
}

function parseMinYear(min?: string): number {
  if (min && dayjs(min).isValid()) return dayjs(min).year();
  return dayjs().year();
}

function clampDraft(
  year: number,
  monthIndex: number,
  day: number,
  min?: string,
): { year: number; monthIndex: number; day: number } {
  let nextYear = year;
  let nextMonth = monthIndex;
  let nextDay = Math.min(day, daysInMonth(nextYear, nextMonth));

  const candidate = toIsoDate(nextYear, nextMonth, nextDay);
  if (min && dayjs(candidate).isBefore(dayjs(min), 'day')) {
    const minDate = dayjs(min);
    nextYear = minDate.year();
    nextMonth = minDate.month();
    nextDay = minDate.date();
  }

  return { year: nextYear, monthIndex: nextMonth, day: nextDay };
}

function formatDisplayDate(value: string): string {
  if (!value || !dayjs(value).isValid()) return '';
  return dayjs(value).locale('en').format('ddd, D MMM YYYY');
}

function useEnglishCalendarDraft(value: string, min: string | undefined, open: boolean) {
  const minDate = min && dayjs(min).isValid() ? dayjs(min) : dayjs().startOf('day');
  const minYear = parseMinYear(min);
  const maxYear = minYear + 2;

  const initialDraft = useMemo(() => {
    const base =
      value && dayjs(value).isValid()
        ? dayjs(value)
        : min && dayjs(min).isValid()
          ? dayjs(min)
          : dayjs().startOf('day');
    return clampDraft(base.year(), base.month(), base.date(), min);
  }, [value, min]);

  const [draftYear, setDraftYear] = useState(initialDraft.year);
  const [draftMonth, setDraftMonth] = useState(initialDraft.monthIndex);
  const [draftDay, setDraftDay] = useState(initialDraft.day);

  useEffect(() => {
    if (!open) return;
    const next = clampDraft(initialDraft.year, initialDraft.monthIndex, initialDraft.day, min);
    setDraftYear(next.year);
    setDraftMonth(next.monthIndex);
    setDraftDay(next.day);
  }, [open, initialDraft, min]);

  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = minYear; y <= maxYear; y += 1) years.push(y);
    return years;
  }, [minYear, maxYear]);

  const dayOptions = useMemo(() => {
    const total = daysInMonth(draftYear, draftMonth);
    const days: number[] = [];
    for (let d = 1; d <= total; d += 1) {
      const iso = toIsoDate(draftYear, draftMonth, d);
      if (!min || !dayjs(iso).isBefore(minDate, 'day')) {
        days.push(d);
      }
    }
    return days.length > 0 ? days : [minDate.date()];
  }, [draftYear, draftMonth, min, minDate]);

  useEffect(() => {
    if (!dayOptions.includes(draftDay)) {
      setDraftDay(dayOptions[0]);
    }
  }, [dayOptions, draftDay]);

  const updateDraft = useCallback(
    (year: number, monthIndex: number, day: number) => {
      const next = clampDraft(year, monthIndex, day, min);
      setDraftYear(next.year);
      setDraftMonth(next.monthIndex);
      setDraftDay(next.day);
    },
    [min],
  );

  const monthCells = useMemo(() => {
    const first = dayjs().year(draftYear).month(draftMonth).date(1);
    const offset = first.day();
    const total = daysInMonth(draftYear, draftMonth);
    const cells: Array<number | null> = [];
    for (let i = 0; i < offset; i += 1) cells.push(null);
    for (let d = 1; d <= total; d += 1) cells.push(d);
    return cells;
  }, [draftYear, draftMonth]);

  return {
    minDate,
    draftYear,
    draftMonth,
    draftDay,
    yearOptions,
    dayOptions,
    monthCells,
    updateDraft,
    selectedIso: toIsoDate(draftYear, draftMonth, draftDay),
  };
}

const WHEEL_ITEM_HEIGHT = 36;
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
const AMPM_OPTIONS = ['AM', 'PM'] as const;

function snapMinute(raw: number): string {
  const snapped = Math.round(raw / 5) * 5;
  const clamped = Math.min(55, Math.max(0, snapped));
  return String(clamped).padStart(2, '0');
}

function parseTimeParts(time: string): { hour: string; minute: string; ampm: string } {
  const match = time.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return { hour: '09', minute: '00', ampm: 'AM' };
  const hourNum = Number(match[1]);
  const hour = String(hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum).padStart(2, '0');
  const minuteNum = Number(match[2]);
  const minute = snapMinute(Number.isFinite(minuteNum) ? minuteNum : 0);
  return {
    hour: HOUR_OPTIONS.includes(hour) ? hour : '09',
    minute: MINUTE_OPTIONS.includes(minute) ? minute : '00',
    ampm: match[3].toUpperCase() === 'PM' ? 'PM' : 'AM',
  };
}

function formatTimeSlot(hour: string, minute: string, ampm: string): string {
  return `${hour}:${minute} ${ampm}`;
}

function PickerSelect({
  value,
  onChange,
  options,
  compact = false,
  menuMinWidth,
}: {
  value: string | number;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string | number; label: string }>;
  compact?: boolean;
  menuMinWidth?: number;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const selected = options.find((opt) => String(opt.value) === String(value)) ?? options[0];

  const toggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const width = Math.max(rect.width, menuMinWidth ?? rect.width);
      const menuHeight = Math.min(192, options.length * 32 + 8);
      const spaceBelow = window.innerHeight - rect.bottom;
      const top =
        spaceBelow < menuHeight + 8 && rect.top > menuHeight + 8
          ? rect.top - menuHeight - 4
          : rect.bottom + 4;
      setMenuPos({ top, left: rect.left, width });
    }
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      close();
    };
    window.addEventListener('resize', close);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  return (
    <div className="w-full">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
        className={`w-full ${compact ? 'h-7 px-1.5' : 'h-8 px-2.5'} rounded-md border text-xs font-normal outline-none bg-white transition-colors flex items-center justify-between gap-1 text-left cursor-pointer ${
          open ? 'border-green-500' : 'border-gray-200'
        }`}
      >
        <span className="min-w-0 flex-1 truncate text-gray-800">{selected?.label ?? ''}</span>
        <i
          className={`ri-arrow-down-s-line text-gray-400 ${compact ? 'text-base' : 'text-lg'} flex-shrink-0 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open
        ? createPortal(
            <>
              <div className="fixed inset-0 z-[80]" onClick={() => setOpen(false)} />
              <div
                ref={menuRef}
                role="listbox"
                className="fixed z-[81] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden py-1 max-h-48 overflow-y-auto"
                style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
              >
                {options.map((opt) => {
                  const active = String(opt.value) === String(value);
                  return (
                    <button
                      key={String(opt.value)}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        onChange(String(opt.value));
                        setOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-xs cursor-pointer flex items-center justify-between gap-2 ${
                        active
                          ? 'bg-green-50 text-green-700 font-semibold'
                          : 'text-gray-700 font-normal hover:bg-gray-50'
                      }`}
                    >
                      <span className="truncate">{opt.label}</span>
                      {active ? <i className="ri-check-line text-green-600 flex-shrink-0" /> : null}
                    </button>
                  );
                })}
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
}

function WheelColumn({
  options,
  value,
  onChange,
  enabled,
}: {
  options: readonly string[];
  value: string;
  onChange: (value: string) => void;
  enabled: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollEndTimer = useRef<number | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const indexOfValue = useCallback(
    (next: string) => {
      const index = options.indexOf(next);
      return index >= 0 ? index : 0;
    },
    [options],
  );

  const [draftIndex, setDraftIndex] = useState(() => indexOfValue(value));

  const scrollToIndex = useCallback((index: number, behavior: ScrollBehavior = 'auto') => {
    const el = scrollRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(options.length - 1, index));
    el.scrollTo({ top: clamped * WHEEL_ITEM_HEIGHT, behavior });
    setDraftIndex(clamped);
  }, [options.length]);

  useEffect(() => {
    if (!enabled) return;
    const frame = requestAnimationFrame(() => {
      scrollToIndex(indexOfValue(valueRef.current));
    });
    return () => cancelAnimationFrame(frame);
  }, [enabled, indexOfValue, scrollToIndex]);

  const handleScroll = useCallback(() => {
    if (scrollEndTimer.current !== null) {
      window.clearTimeout(scrollEndTimer.current);
    }
    scrollEndTimer.current = window.setTimeout(() => {
      const el = scrollRef.current;
      if (!el || options.length === 0) return;
      const index = Math.max(0, Math.min(options.length - 1, Math.round(el.scrollTop / WHEEL_ITEM_HEIGHT)));
      scrollToIndex(index, 'smooth');
      const next = options[index];
      if (next && next !== valueRef.current) onChange(next);
    }, 80);
  }, [onChange, options, scrollToIndex]);

  useEffect(
    () => () => {
      if (scrollEndTimer.current !== null) window.clearTimeout(scrollEndTimer.current);
    },
    [],
  );

  const padHeight = `calc(50% - ${WHEEL_ITEM_HEIGHT / 2}px)`;

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="flex-1 min-w-0 h-full overflow-y-auto snap-y snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
    >
      <div style={{ height: padHeight }} />
      {options.map((option, index) => {
        const active = index === draftIndex;
        return (
          <button
            key={option}
            type="button"
            onClick={() => {
              scrollToIndex(index, 'smooth');
              onChange(option);
            }}
            className="w-full snap-center flex items-center justify-center cursor-pointer"
            style={{ height: WHEEL_ITEM_HEIGHT }}
          >
            <span
              className={`leading-none transition-colors ${
                active ? 'text-green-600 font-semibold text-xs' : 'text-gray-400 font-normal text-[10px]'
              }`}
            >
              {option}
            </span>
          </button>
        );
      })}
      <div style={{ height: padHeight }} />
    </div>
  );
}

function EnglishCalendarBody({
  min,
  draftYear,
  draftMonth,
  draftDay,
  yearOptions,
  dayOptions,
  monthCells,
  minDate,
  updateDraft,
  compact = false,
}: {
  min?: string;
  draftYear: number;
  draftMonth: number;
  draftDay: number;
  yearOptions: number[];
  dayOptions: number[];
  monthCells: Array<number | null>;
  minDate: dayjs.Dayjs;
  updateDraft: (year: number, monthIndex: number, day: number) => void;
  compact?: boolean;
}) {
  const dayHeight = compact ? 'h-7' : 'h-8';
  const dayText = compact ? 'text-[11px]' : 'text-xs';

  return (
    <>
      <CalendarSelectRow
        compact={compact}
        draftYear={draftYear}
        draftMonth={draftMonth}
        draftDay={draftDay}
        yearOptions={yearOptions}
        dayOptions={dayOptions}
        updateDraft={updateDraft}
      />
      <CalendarMonthGrid
        compact={compact}
        min={min}
        draftYear={draftYear}
        draftMonth={draftMonth}
        draftDay={draftDay}
        monthCells={monthCells}
        minDate={minDate}
        updateDraft={updateDraft}
        dayHeight={dayHeight}
        dayText={dayText}
      />
    </>
  );
}

function CalendarSelectRow({
  compact,
  draftYear,
  draftMonth,
  draftDay,
  yearOptions,
  dayOptions,
  updateDraft,
}: {
  compact: boolean;
  draftYear: number;
  draftMonth: number;
  draftDay: number;
  yearOptions: number[];
  dayOptions: number[];
  updateDraft: (year: number, monthIndex: number, day: number) => void;
}) {
  return (
    <div className={`grid grid-cols-3 ${compact ? 'gap-1.5 mb-2' : 'gap-2 mb-3'}`}>
      <div>
        <div className="text-[10px] font-normal text-gray-400 mb-1">Month</div>
        <PickerSelect
          compact={compact}
          value={draftMonth}
          onChange={(next) => updateDraft(draftYear, Number(next), draftDay)}
          menuMinWidth={128}
          options={ENGLISH_MONTHS.map((label, index) => ({ value: index, label }))}
        />
      </div>
      <div>
        <div className="text-[10px] font-normal text-gray-400 mb-1">Day</div>
        <PickerSelect
          compact={compact}
          value={draftDay}
          onChange={(next) => updateDraft(draftYear, draftMonth, Number(next))}
          options={dayOptions.map((day) => ({ value: day, label: String(day) }))}
        />
      </div>
      <div>
        <div className="text-[10px] font-normal text-gray-400 mb-1">Year</div>
        <PickerSelect
          compact={compact}
          value={draftYear}
          onChange={(next) => updateDraft(Number(next), draftMonth, draftDay)}
          options={yearOptions.map((year) => ({ value: year, label: String(year) }))}
        />
      </div>
    </div>
  );
}

function CalendarMonthGrid({
  compact,
  min,
  draftYear,
  draftMonth,
  draftDay,
  monthCells,
  minDate,
  updateDraft,
  dayHeight,
  dayText,
}: {
  compact: boolean;
  min?: string;
  draftYear: number;
  draftMonth: number;
  draftDay: number;
  monthCells: Array<number | null>;
  minDate: dayjs.Dayjs;
  updateDraft: (year: number, monthIndex: number, day: number) => void;
  dayHeight: string;
  dayText: string;
}) {
  return (
    <div>
      <div className={`grid grid-cols-7 ${compact ? 'gap-0.5 mb-1' : 'gap-1 mb-1.5'}`}>
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="text-center text-[10px] font-medium text-gray-400 py-0.5">
            {label}
          </div>
        ))}
      </div>
      <div className={`grid grid-cols-7 ${compact ? 'gap-0.5' : 'gap-1'}`}>
        {monthCells.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className={dayHeight} />;
          }

          const iso = toIsoDate(draftYear, draftMonth, day);
          const disabled = Boolean(min && dayjs(iso).isBefore(minDate, 'day'));
          const selected = draftDay === day;

          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              onClick={() => updateDraft(draftYear, draftMonth, day)}
              className={`${dayHeight} rounded-md ${dayText} font-medium transition-colors ${
                disabled
                  ? 'text-gray-300 cursor-not-allowed'
                  : selected
                    ? 'bg-green-600 text-white'
                    : 'text-gray-700 hover:bg-green-50 cursor-pointer'
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function EnglishDateInput({
  id,
  value,
  onChange,
  min,
  placeholder = 'Select date',
  pickerTitle = 'Select Date',
  className = '',
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: EnglishDateInputProps) {
  const [open, setOpen] = useState(false);
  const calendar = useEnglishCalendarDraft(value, min, open);
  const displayValue = formatDisplayDate(value);

  const closePicker = useCallback(() => setOpen(false), []);

  const applyDraft = useCallback(() => {
    onChange(calendar.selectedIso);
    setOpen(false);
  }, [calendar.selectedIso, onChange]);

  return (
    <>
      <button
        id={id}
        type="button"
        onClick={() => setOpen(true)}
        lang="en"
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className={`${TRIGGER_CLASS} ${displayValue ? 'text-gray-800' : 'text-gray-400'} ${className}`}
      >
        <i className="ri-calendar-event-line text-green-500 text-sm flex-shrink-0" />
        <span className="flex-1 truncate">{displayValue || placeholder}</span>
        <i className="ri-arrow-down-s-line text-gray-400 text-sm flex-shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={closePicker} />
          <div
            className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl border-t border-gray-200 px-4 pt-4 pb-8 max-h-[85vh] overflow-y-auto"
            lang="en"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-gray-800 font-semibold text-sm">{pickerTitle}</div>
                <div className="text-gray-400 text-xs mt-0.5">Select a date in English format</div>
              </div>
              <button
                type="button"
                onClick={closePicker}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            <EnglishCalendarBody min={min} {...calendar} />

            <button
              type="button"
              onClick={applyDraft}
              className="ui-btn-sm w-full bg-green-500 hover:bg-green-600 text-white mt-4"
            >
              Confirm
            </button>
          </div>
        </>
      )}
    </>
  );
}

export function EnglishDateTimeInput({
  id,
  date,
  time,
  onChange,
  min,
  placeholder = 'Select date and time',
  pickerTitle = 'Appointment',
  className = '',
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: EnglishDateTimeInputProps) {
  const [open, setOpen] = useState(false);
  const calendar = useEnglishCalendarDraft(date, min, open);
  const initialParts = parseTimeParts(time || '09:00 AM');

  const [draftHour, setDraftHour] = useState(initialParts.hour);
  const [draftMinute, setDraftMinute] = useState(initialParts.minute);
  const [draftAmpm, setDraftAmpm] = useState(initialParts.ampm);
  const calendarBodyRef = useRef<HTMLDivElement>(null);
  const [timeBodyHeight, setTimeBodyHeight] = useState<number>(0);

  useEffect(() => {
    if (!open) return;
    const parts = parseTimeParts(time || '09:00 AM');
    setDraftHour(parts.hour);
    setDraftMinute(parts.minute);
    setDraftAmpm(parts.ampm);
  }, [open, time]);

  useLayoutEffect(() => {
    if (!open) return;
    const el = calendarBodyRef.current;
    if (!el) return;

    const syncHeight = () => {
      const next = Math.round(el.getBoundingClientRect().height);
      if (next > 0) setTimeBodyHeight(next);
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, calendar.draftMonth, calendar.draftYear, calendar.monthCells]);

  const displayDate = formatDisplayDate(date);
  const displayValue = displayDate && time ? `${displayDate} · ${time}` : '';
  const draftTime = formatTimeSlot(draftHour, draftMinute, draftAmpm);

  const closePicker = useCallback(() => setOpen(false), []);

  const applyDraft = useCallback(() => {
    onChange(calendar.selectedIso, draftTime);
    setOpen(false);
  }, [calendar.selectedIso, draftTime, onChange]);

  return (
    <>
      <button
        id={id}
        type="button"
        onClick={() => setOpen(true)}
        lang="en"
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className={`${TRIGGER_CLASS} ${displayValue ? 'text-gray-800' : 'text-gray-400'} ${className}`}
      >
        <i className="ri-calendar-schedule-line text-green-500 text-sm flex-shrink-0" />
        <span className="flex-1 truncate">{displayValue || placeholder}</span>
        <i className="ri-arrow-down-s-line text-gray-400 text-sm flex-shrink-0" />
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 bg-black/40 z-[70]" onClick={closePicker} />
          <div
            className="fixed inset-x-0 bottom-0 z-[71] bg-white rounded-t-2xl border-t border-gray-200 px-4 pt-4 pb-8 max-h-[85vh] overflow-y-auto"
            lang="en"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-gray-800 font-semibold text-sm">{pickerTitle}</div>
                <div className="text-gray-400 text-xs mt-0.5">Select date and time</div>
              </div>
              <button
                type="button"
                onClick={closePicker}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            <div className="flex gap-2 items-start">
              <div className="min-w-0 flex-1">
                <CalendarSelectRow
                  compact
                  draftYear={calendar.draftYear}
                  draftMonth={calendar.draftMonth}
                  draftDay={calendar.draftDay}
                  yearOptions={calendar.yearOptions}
                  dayOptions={calendar.dayOptions}
                  updateDraft={calendar.updateDraft}
                />
                <div ref={calendarBodyRef}>
                  <CalendarMonthGrid
                    compact
                    min={min}
                    draftYear={calendar.draftYear}
                    draftMonth={calendar.draftMonth}
                    draftDay={calendar.draftDay}
                    monthCells={calendar.monthCells}
                    minDate={calendar.minDate}
                    updateDraft={calendar.updateDraft}
                    dayHeight="h-7"
                    dayText="text-[11px]"
                  />
                </div>
              </div>

              <div className="w-[4.75rem] flex-shrink-0">
                <div className="mb-2">
                  <div className="text-[10px] font-normal text-gray-400 mb-1">AM/PM</div>
                  <PickerSelect
                    compact
                    value={draftAmpm}
                    onChange={setDraftAmpm}
                    options={AMPM_OPTIONS.map((opt) => ({ value: opt, label: opt }))}
                  />
                </div>
                <div
                  className="flex flex-col min-h-0"
                  style={{ height: timeBodyHeight || undefined }}
                >
                  <div className="flex h-4 mb-1">
                    <span className="flex-1 text-center text-[10px] font-normal text-gray-400">Hr</span>
                    <span className="flex-1 text-center text-[10px] font-normal text-gray-400">Min</span>
                  </div>
                  <div className="relative flex-1 min-h-0 overflow-hidden rounded-md bg-gray-50 border border-gray-100">
                    <div className="pointer-events-none absolute inset-x-0.5 top-1/2 -translate-y-1/2 h-9 rounded-md border border-green-500 bg-green-50 z-0" />
                    <div className="relative z-10 flex h-full">
                      <WheelColumn
                        options={HOUR_OPTIONS}
                        value={draftHour}
                        onChange={setDraftHour}
                        enabled={open}
                      />
                      <WheelColumn
                        options={MINUTE_OPTIONS}
                        value={draftMinute}
                        onChange={setDraftMinute}
                        enabled={open}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={applyDraft}
              className="ui-btn-sm w-full bg-green-500 hover:bg-green-600 text-white mt-4"
            >
              Confirm
            </button>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
